import { MessageCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import profilePlaceholder from "../assets/profile-placeholder.svg";
import {
  deleteJobPostComment,
  isApiConfigured,
  listJobPostComments,
  listPublicJobPostComments,
  postJobPostComment,
} from "../apiClient";

function formatCommentTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mapApiCommentToState(c) {
  return {
    id: c.id,
    author: String(c.authorDisplayName || "").trim() || "Member",
    authorUserId: String(c.authorUserId || ""),
    authorAvatarUrl: String(c.authorAvatarUrl || "").trim(),
    isJobOwner: Boolean(c.isJobOwner),
    createdAt: c.createdAt,
    body: String(c.body || ""),
    replies: Array.isArray(c.replies) ? c.replies.map(mapApiCommentToState) : [],
  };
}

function commentDeleteIconSvg(className = "job-comment-delete-icon") {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="m18 7-.886 10.342c-.111 1.29-.166 1.936-.453 2.424a2.5 2.5 0 0 1-1.078.99c-.511.244-1.16.244-2.455.244h-2.256c-1.296 0-1.944 0-2.455-.244a2.5 2.5 0 0 1-1.078-.99c-.287-.488-.342-1.134-.453-2.424L6 7m-1.5-.5h4.615m0 0 .386-2.672c.112-.486.516-.828.98-.828h3.038c.464 0 .867.342.98.828l.386 2.672m-5.77 0h5.77m0 0H19.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * @param {object} props
 * @param {string} props.jobPostId
 * @param {object | null} props.currentUser
 * @param {string} props.authToken
 * @param {"detail" | "panel"} props.layout — detail: h2 + job-section-title; panel: h4 + job-section-subtitle
 * @param {string} [props.publicSlugOrId] — when set with no signed-in user, load comments via public API (read-only).
 */
export default function JobPostCommentsSection({
  jobPostId,
  currentUser,
  authToken,
  layout = "detail",
  publicSlugOrId,
}) {
  const effectiveToken = String(
    authToken || (typeof window !== "undefined" ? window.localStorage.getItem("jobuwin-auth-token") : "") || ""
  ).trim();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newCommentText, setNewCommentText] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [actionBusy, setActionBusy] = useState(false);

  const currentUserId = currentUser?.id != null ? String(currentUser.id) : "";
  const isGuestPublicRead =
    Boolean(String(publicSlugOrId || "").trim()) && !currentUser && isApiConfigured();
  const canUseComments =
    Boolean(currentUser) &&
    !isGuestPublicRead &&
    isApiConfigured() &&
    Boolean(effectiveToken) &&
    (currentUser?.role === "applicant" || currentUser?.role === "hiring-manager" || currentUser?.role === "recruiter");

  const reloadComments = useCallback(async () => {
    const pubKey = String(publicSlugOrId || "").trim();
    if (pubKey && !currentUser && isApiConfigured()) {
      setLoading(true);
      setError("");
      try {
        const { comments: rows } = await listPublicJobPostComments(pubKey);
        setComments((rows || []).map(mapApiCommentToState));
      } catch (e) {
        setComments([]);
        setError(e instanceof Error ? e.message : "Could not load comments.");
      } finally {
        setLoading(false);
      }
      return;
    }
    const id = String(jobPostId || "").trim();
    const t = effectiveToken;
    if (!id || !t || !isApiConfigured()) {
      setComments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { comments: rows } = await listJobPostComments(id, t);
      setComments((rows || []).map(mapApiCommentToState));
    } catch (e) {
      setComments([]);
      setError(e instanceof Error ? e.message : "Could not load comments.");
    } finally {
      setLoading(false);
    }
  }, [jobPostId, effectiveToken, publicSlugOrId, currentUser]);

  useEffect(() => {
    setNewCommentText("");
    setReplyDrafts({});
    if (isGuestPublicRead) {
      void reloadComments();
      return undefined;
    }
    if (!canUseComments) {
      setComments([]);
      setLoading(false);
      setError("");
      return undefined;
    }
    void reloadComments();
    return undefined;
  }, [canUseComments, isGuestPublicRead, jobPostId, reloadComments]);

  const canDelete = (row) =>
    canUseComments && currentUserId && String(row.authorUserId) === currentUserId;

  const handlePostTop = async () => {
    const id = String(jobPostId || "").trim();
    const text = newCommentText.trim();
    if (!text || !effectiveToken || actionBusy) return;
    setActionBusy(true);
    setError("");
    try {
      await postJobPostComment(id, effectiveToken, { body: text });
      setNewCommentText("");
      await reloadComments();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not post comment.");
    } finally {
      setActionBusy(false);
    }
  };

  const handlePostReply = async (parentCommentId) => {
    const id = String(jobPostId || "").trim();
    const text = (replyDrafts[parentCommentId] || "").trim();
    if (!text || !effectiveToken || actionBusy) return;
    setActionBusy(true);
    setError("");
    try {
      await postJobPostComment(id, effectiveToken, { body: text, parentId: parentCommentId });
      setReplyDrafts((d) => ({ ...d, [parentCommentId]: "" }));
      await reloadComments();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not post reply.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!effectiveToken || actionBusy) return;
    setActionBusy(true);
    setError("");
    try {
      await deleteJobPostComment(String(commentId), effectiveToken);
      await reloadComments();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete comment.");
    } finally {
      setActionBusy(false);
    }
  };

  if (!currentUser && !isGuestPublicRead) return null;

  const isDetail = layout === "detail";
  const titleClass = layout === "panel" ? "job-section-subtitle" : undefined;
  const headingId = `job-comments-heading-${String(jobPostId || publicSlugOrId || "job")}`;

  const composerBlock = canUseComments ? (
    <div className="job-comments-composer">
      <label htmlFor={`new-job-comment-${jobPostId}`} className="job-comments-label">
        Your comment
      </label>
      <textarea
        id={`new-job-comment-${jobPostId}`}
        className="job-comments-textarea"
        value={newCommentText}
        onChange={(event) => setNewCommentText(event.target.value)}
        placeholder="Write a comment…"
        rows={3}
        disabled={actionBusy}
      />
      <button
        type="button"
        className="job-comments-post-btn"
        onClick={() => void handlePostTop()}
        disabled={actionBusy || !newCommentText.trim()}
      >
        Post comment
      </button>
    </div>
  ) : (
    <p className="job-comments-readonly-note">Sign in to post a comment or reply.</p>
  );

  const feedBlock = loading ? (
    <p className="job-comments-empty" aria-live="polite">
      Loading comments…
    </p>
  ) : comments.length === 0 ? (
    <p className={`job-comments-empty${isDetail ? " job-comments-empty--detail" : ""}`}>
      {isDetail ? (
        <>
          <MessageCircle className="job-comments-empty__icon nd1-icon" size={22} strokeWidth={1.75} aria-hidden="true" />
          No comments yet. Be the first to leave a comment.
        </>
      ) : (
        "No comments yet."
      )}
    </p>
  ) : (
    <ul className="job-comments-list">
      {comments.map((comment) => (
        <li key={comment.id} className="job-comment-card">
          <div className="job-comment-main-row">
            {canDelete(comment) ? (
              <button
                type="button"
                className="job-comment-delete-btn"
                onClick={() => void handleDelete(comment.id)}
                disabled={actionBusy}
                aria-label="Delete comment"
              >
                {commentDeleteIconSvg()}
              </button>
            ) : (
              <span className="job-comment-delete-spacer" aria-hidden="true" />
            )}
            <div className="job-comment-main-content">
              <div className="job-comment-author-block">
                <img
                  className="job-comment-avatar"
                  src={comment.authorAvatarUrl || profilePlaceholder}
                  alt=""
                  width={40}
                  height={40}
                  loading="lazy"
                />
                <div className="job-comment-author-text">
                  <div className="job-comment-name-row">
                    <strong className="job-comment-author-name">{comment.author}</strong>
                    {comment.isJobOwner ? (
                      <span className="job-comment-owner-badge">Job Owner</span>
                    ) : null}
                    {comment.createdAt ? (
                      <>
                        <span className="job-comment-meta-sep" aria-hidden="true">
                          ·
                        </span>
                        <time className="job-comment-time" dateTime={String(comment.createdAt)}>
                          {formatCommentTime(comment.createdAt)}
                        </time>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
              <p className="job-comment-body">{comment.body}</p>
            </div>
          </div>
          <div className="job-comment-replies">
            <h3 className="job-replies-heading">Replies</h3>
            {comment.replies.length > 0 ? (
              <ul className="job-replies-list">
                {comment.replies.map((reply) => (
                  <li key={reply.id} className="job-reply-item">
                    <div className="job-comment-main-row job-comment-main-row--reply">
                      {canDelete(reply) ? (
                        <button
                          type="button"
                          className="job-comment-delete-btn job-comment-delete-btn--reply"
                          onClick={() => void handleDelete(reply.id)}
                          disabled={actionBusy}
                          aria-label="Delete reply"
                        >
                          {commentDeleteIconSvg()}
                        </button>
                      ) : (
                        <span className="job-comment-delete-spacer" aria-hidden="true" />
                      )}
                      <div className="job-reply-main">
                        <div className="job-comment-author-block job-comment-author-block--compact">
                          <img
                            className="job-comment-avatar job-comment-avatar--sm"
                            src={reply.authorAvatarUrl || profilePlaceholder}
                            alt=""
                            width={32}
                            height={32}
                            loading="lazy"
                          />
                          <div className="job-comment-author-text">
                            <div className="job-comment-name-row job-comment-name-row--reply">
                              <strong className="job-comment-author-name">{reply.author}</strong>
                              {reply.isJobOwner ? (
                                <span className="job-comment-owner-badge">Job Owner</span>
                              ) : null}
                              {reply.createdAt ? (
                                <>
                                  <span className="job-comment-meta-sep" aria-hidden="true">
                                    ·
                                  </span>
                                  <time className="job-comment-time" dateTime={String(reply.createdAt)}>
                                    {formatCommentTime(reply.createdAt)}
                                  </time>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <p className="job-reply-body">{reply.body}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="job-replies-empty">No replies yet.</p>
            )}
            {canUseComments ? (
              <div className="job-reply-composer">
                <label htmlFor={`reply-${jobPostId}-${comment.id}`} className="job-comments-label">
                  Your reply
                </label>
                <textarea
                  id={`reply-${jobPostId}-${comment.id}`}
                  className="job-comments-textarea"
                  rows={2}
                  value={replyDrafts[comment.id] || ""}
                  onChange={(event) =>
                    setReplyDrafts((d) => ({ ...d, [comment.id]: event.target.value }))
                  }
                  placeholder="Write a reply…"
                  disabled={actionBusy}
                />
                <button
                  type="button"
                  className="job-comments-post-btn job-comments-post-btn--secondary"
                  onClick={() => void handlePostReply(comment.id)}
                  disabled={actionBusy || !(replyDrafts[comment.id] || "").trim()}
                >
                  Post reply
                </button>
              </div>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <section
      className={`job-comments-section${isDetail ? " job-comments-section--detail" : ""}`}
      aria-labelledby={headingId}
    >
      {layout === "panel" ? (
        <h4 id={headingId} className={titleClass}>
          Comments
        </h4>
      ) : (
        <h3 id={headingId}>
          Comments
        </h3>
      )}
      {error ? (
        <p className="job-comments-error" role="alert">
          {error}
        </p>
      ) : null}
      {canUseComments || isGuestPublicRead ? (
        <div className={`job-comments-section__body${isDetail ? " job-comments-section__body--detail" : ""}`}>
          {composerBlock}
          {feedBlock}
        </div>
      ) : null}
    </section>
  );
}
